import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiMenu, FiX, FiPhone, FiMail } from "react-icons/fi";
import { FaYoutube, FaInstagram, FaFacebookF } from "react-icons/fa";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Facilities", path: "/facilities" },
    { name: "Gallery", path: "/gallery" },
  ];

  return (
    <>
      {/* Top bar — for a formal school look */}
      <div
        className="fixed top-0 left-0 w-full h-[32px] z-[60] hidden md:flex items-center justify-between px-10 transition-transform duration-500"
        style={{
          background: "rgba(2, 8, 16, 0.98)",
          borderBottom: "1px solid rgba(244, 200, 66, 0.15)",
          transform: scrolled ? "translateY(-100%)" : "translateY(0)",
        }}
      >
        <div className="flex items-center gap-6">
          <a
            href="tel:+919041321493"
            className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-gold transition-colors"
          >
            <FiPhone className="text-gold" />
            <span>+91 9041321493</span>
          </a>
          <a
            href="mailto:info@galaxystarschool.com"
            className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-l border-white/10 pl-6 hover:text-gold transition-colors"
          >
            <FiMail className="text-gold" />
            <span>info@galaxystarschool.com</span>
          </a>
          <div className="flex items-center gap-2 text-[10px] font-bold text-gold uppercase tracking-[0.2em] border-l border-white/10 pl-6">
            <span>Affiliation: PU-158</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <a
            href="https://www.facebook.com/p/Galaxy-Star-Public-School-Sekhwan-100057501486855/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="text-gray-400 hover:text-gold transition-colors cursor-pointer"
          >
            <FaFacebookF size={12} />
          </a>
          <a
            href="https://www.instagram.com/galaxystarschool/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="text-gray-400 hover:text-gold transition-colors cursor-pointer"
          >
            <FaInstagram size={12} />
          </a>
          <a
            href="https://www.youtube.com/@galaxystarschool"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
            className="text-gray-400 hover:text-gold transition-colors cursor-pointer"
          >
            <FaYoutube size={12} />
          </a>
        </div>
      </div>

      <nav
        className={`fixed left-0 w-full h-[68px] z-50 flex items-center justify-between px-5 md:px-10 transition-all duration-500 ${
          scrolled ? "top-0" : "top-0 md:top-[32px]"
        }`}
        style={{
          background: scrolled ? "rgba(2, 8, 16, 0.96)" : "rgba(2, 8, 16, 0.85)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: scrolled
            ? "1px solid rgba(244, 200, 66, 0.15)"
            : "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: scrolled ? "0 8px 40px rgba(0,0,0,0.6)" : "none",
        }}
      >
        {/* Logo + School Name */}
        <Link
          to="/"
          className="flex items-center gap-3 group"
          onClick={() => setMenuOpen(false)}
        >
          <div className="relative flex-shrink-0">
            <div
              className="absolute inset-0 rounded-full blur-md opacity-60 pulse-gold"
              style={{ background: "rgba(244,200,66,0.35)", margin: "-3px" }}
            />
            <div
              className="relative rounded-full p-[2px]"
              style={{
                background:
                  "linear-gradient(135deg, #f4c842 0%, #fde68a 50%, #e8ad10 100%)",
              }}
            >
              <div className="rounded-full bg-[#020810] p-[2px]">
                <img
                  src="/renamed_website_images/school-logo.png"
                  alt="Galaxy Star Logo"
                  className="h-9 w-9 object-contain rounded-full"
                />
              </div>
            </div>
          </div>

          <div className="leading-tight">
            <span
              className="block text-[#f0f5ff] text-[11px] sm:text-xs md:text-sm font-black uppercase tracking-[0.12em]"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              Galaxy Star
            </span>
            <span
              className="block text-[9px] sm:text-[10px] md:text-[11px] font-semibold tracking-[0.18em] uppercase"
              style={{
                fontFamily: "Nunito, sans-serif",
                color: "rgba(244,200,66,0.75)",
              }}
            >
              Public School
            </span>
          </div>
        </Link>

        {/* Desktop Nav Links */}
        <ul className="hidden md:flex items-center gap-8">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path} className="relative">
                <Link
                  to={item.path}
                  className="text-xs tracking-[0.1em] uppercase font-bold transition-all duration-300"
                  style={{
                    fontFamily: "Nunito, sans-serif",
                    color: isActive ? "#f4c842" : "#8899b8",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.color = "#f0f5ff";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.color = "#8899b8";
                  }}
                >
                  {item.name}
                </Link>
                {isActive && (
                  <span
                    className="absolute -bottom-1 left-0 w-full h-[2px] rounded-full"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, #f4c842, transparent)",
                    }}
                  />
                )}
              </li>
            );
          })}

          {/* Enroll CTA button */}
          <li>
            <Link
              to="/enroll"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-black tracking-[0.15em] uppercase transition-all duration-300 hover:scale-[1.02]"
              style={{
                fontFamily: "Nunito, sans-serif",
                background: "linear-gradient(135deg, #f4c842, #e8ad10)",
                color: "#020810",
              }}
            >
              Enroll Now
            </Link>
          </li>
        </ul>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-2xl p-2 z-50 transition-colors duration-200"
          style={{ color: menuOpen ? "#f4c842" : "#f0f5ff" }}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <FiX /> : <FiMenu />}
        </button>
      </nav>

      {/* Mobile Side Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-72 z-40 transform transition-transform duration-300 ease-in-out"
        style={{
          transform: menuOpen ? "translateX(0)" : "translateX(100%)",
          background: "rgba(2, 8, 20, 0.98)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderLeft: "1px solid rgba(244,200,66,0.1)",
        }}
      >
        {/* Decorative nebula glow */}
        <div
          className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(244,200,66,0.08) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute bottom-10 left-0 w-32 h-32 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)",
          }}
        />

        <div className="pt-24 px-7">
          <ul className="flex flex-col gap-6">
            {[...navItems, { name: "Apply & Enroll", path: "/enroll" }].map(
              (item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="flex items-center justify-between w-full text-xs tracking-[0.1em] uppercase font-bold transition-all duration-300"
                      style={{
                        fontFamily: "Nunito, sans-serif",
                        color: isActive ? "#f4c842" : "#8899b8",
                      }}
                      onClick={() => setMenuOpen(false)}
                    >
                      <span>{item.name}</span>
                      <HiOutlineArrowNarrowRight
                        className="text-base"
                        style={{
                          color: "#f4c842",
                          opacity: isActive ? 1 : 0.5,
                        }}
                      />
                    </Link>
                  </li>
                );
              },
            )}
          </ul>
        </div>
      </div>

      {/* Backdrop overlay when mobile menu is open */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Navbar;
